import { CloudinaryImg } from "@components/CloudinaryImg";
import { ReferredNonprofitCard } from "@components/Nonprofit/ReferredNonprofitCard";
import { ExistingUserLoginLink } from "@components/QuestionAnswerLink";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { TypeOf } from "io-ts";
import { useRouter } from "next/router";
import React, { useCallback, useContext, useState } from "react";

import { NonprofitAdminReferralCampaignResponse } from "@every.org/common/src/codecs/entities";
import {
  NonprofitAdminReferralType,
  VerifyEmailStatus,
} from "@every.org/common/src/entity/types";
import {
  formatEinWithDash,
  isValidEin,
} from "@every.org/common/src/entity/types/ein";
import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { ImageFormat } from "@every.org/common/src/helpers/cloudinary";
import { removeUndefinedOrNullValues } from "@every.org/common/src/helpers/objectUtilities";
import {
  verifyEmailResponseCodec,
  verifyEmailRouteSpec,
} from "@every.org/common/src/routes/me";
import { getReferralCampaignSpec } from "@every.org/common/src/routes/referralCampaign";

import { Button, ButtonRole, ButtonTargetKind } from "src/components/Button";
import { ContactUsLink, Link } from "src/components/Link";
import { REQUEST_ADMIN_QUERY_PARAM } from "src/components/Nonprofit/EditForm";
import { MAX_CARD_WIDTH_RAW } from "src/components/ScrollingFeedLandingPage";
import { NonprofitCard as NewNonprofitCard } from "src/components/feed/NewNonprofitCard";
import { DefaultPageLayout } from "src/components/layout/DefaultPageLayout";
import { CardPageSection } from "src/components/layout/PageSection";
import { AuthContext } from "src/context/AuthContext";
import {
  setSignupNonprofitAdminRequestCookie,
  updateAuthState,
} from "src/context/AuthContext/actions";
import {
  useIsLoggedIn,
  useLoggedInUserOrUndefined,
} from "src/context/AuthContext/hooks";
import { AuthStatus } from "src/context/AuthContext/types";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { ApiError } from "src/errors/ApiError";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { verticalStackCss, spacing } from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";
import { getWindow } from "src/utility/window";

const NP_CARD_BG_TOP_CLOUDINARY_ID = "admin_np_card_bg_top_right_fh3vho";
const NP_CARD_BG_BOTTOM_CLOUDINARY_ID = "admin_np_card_bg_bottom_left_lrwbw8";

//
// Styles
//
//#region
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: ${spacing.xxxl};
  grid-row-gap: ${spacing.xl};

  align-items: center;

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      grid-template-columns: 1fr 1fr;
    `,
  })}
`;

const NonprofitCardContainer = styled.div`
  ${verticalStackCss.m};
  align-items: center;
  max-width: calc(${MAX_CARD_WIDTH_RAW}px + 100px);
  width: 100%;
  align-self: stretch;
  margin: 0 auto;
  overflow: hidden;
  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      padding: ${spacing.xxxl};
      margin: 0 0 0 auto;
    `,
  })}
`;

const Headline = styled.h1`
  ${textSizeCss.l}

  & > span {
    ${textSizeCss.xl};

    ${cssForMediaSize({
      min: MediaSize.LARGE,
      css: css`
        ${textSizeCss.xxl};
      `,
    })}
  }
`;
//#endregion

interface NonprofitOnboardingIntroProps {
  nonprofit: ContextNonprofit;
}

const NonprofitOnboardingIntro = ({
  nonprofit,
}: NonprofitOnboardingIntroProps) => {
  const { getPathnameWithParams } = useEdoRouter();
  const { params } = getPathnameWithParams();
  const isLoggedIn = useIsLoggedIn();
  const authState = useContext(AuthContext);
  const loggedInUserOrUndefined = useLoggedInUserOrUndefined();
  const router = useRouter();
  const inviteToken = params?.inviteToken;
  const nonce = params?.nonce;
  const referral = params?.referral;
  const [campaign, setCampaign] =
    useState<NonprofitAdminReferralCampaignResponse>();

  const verifyEmail = useCallback(() => {
    if (isLoggedIn && (nonce || inviteToken)) {
      // TODO: we could could call consumeAdminInviteTokenRouteSpec when there is no nonce too
      return queryApi(verifyEmailRouteSpec, {
        body: {
          nonce,
          adminInviteToken: inviteToken,
        },
        routeTokens: {},
        queryParams: {},
      });
    }
    return Promise.resolve(null);
  }, [isLoggedIn, nonce, inviteToken]);
  const handleVerifyEmailResponse = useCallback(
    (params: TypeOf<typeof verifyEmailRouteSpec.responseBodyCodec> | null) => {
      if (params?.status === VerifyEmailStatus.VERIFIED && params.user) {
        // This will re-render the page since authState changes.
        updateAuthState(params.user);
      }
    },
    []
  );
  const handleVerifyEmailError = useCallback((e: Error) => {
    let verifyStatus: string | undefined = undefined;
    if (e instanceof ApiError) {
      const validationResult = verifyEmailResponseCodec.decode(e.data.data);
      if (validationResult._tag === "Right") {
        verifyStatus = validationResult.right.status;
      }
    }
    logger.error({
      message: "An error occurred verifying email",
      error: e,
      data: { verifyStatus },
    });
  }, []);
  useAsyncEffect({
    asyncOperation: verifyEmail,
    handleResponse: handleVerifyEmailResponse,
    handleError: handleVerifyEmailError,
  });

  useAsyncEffect({
    asyncOperation: useCallback(() => {
      if (referral) {
        return queryApi(getReferralCampaignSpec, {
          routeTokens: { referralCode: referral },
          queryParams: {},
          body: {},
        });
      }
      return Promise.resolve(null);
    }, [referral]),
    handleResponse: useCallback(
      (response: NonprofitAdminReferralCampaignResponse | null) => {
        if (response) {
          setCampaign(response);
        }
      },
      [setCampaign]
    ),
    handleError: useCallback(
      (error) => {
        logger.error({
          error,
          message: `Error fetching referral campaign via referral code`,
          data: { referralCode: referral },
        });
        setCampaign(undefined);
      },
      [referral]
    ),
  });

  const nextAction = useCallback(() => {
    const adminRequestPath = getRoutePath({
      name: ClientRouteName.NONPROFIT_ADMIN,
      format: URLFormat.RELATIVE,
      tokens: {
        nonprofitSlug: nonprofit.primarySlug,
      },
      query: {
        ...(referral ? { referral } : {}),
        [REQUEST_ADMIN_QUERY_PARAM]: "true",
      },
    });
    if (isLoggedIn) {
      router.push(adminRequestPath);
    } else {
      const nextLink = getRoutePath({
        name: ClientRouteName.SIGNUP,
        format: URLFormat.RELATIVE,
        query: removeUndefinedOrNullValues({
          redirectUrl: adminRequestPath,
          adminRequest: nonprofit.id,
          inviteToken,
          nonce,
          nonprofitName: nonprofit.name,
          referral,
        }),
      });
      setSignupNonprofitAdminRequestCookie({ nonprofitId: nonprofit.id });
      router.push(nextLink);
    }
  }, [isLoggedIn, router, nonprofit, inviteToken, nonce, referral]);

  const userAdminForLength =
    loggedInUserOrUndefined &&
    loggedInUserOrUndefined.adminFor?.filter(
      (admin) => admin.nonprofit.id === nonprofit.id
    ).length;
  if (loggedInUserOrUndefined && userAdminForLength && userAdminForLength > 0) {
    const adminLink = getRoutePath({
      name: ClientRouteName.NONPROFIT_ADMIN,
      tokens: {
        nonprofitSlug: nonprofit.primarySlug,
      },
      format: URLFormat.RELATIVE,
    });
    router.push(adminLink);
  }

  const window = getWindow();
  const redirectUrl = window
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : "/";

  return (
    <DefaultPageLayout
      pageTitle={`Become an admin`}
      css={[
        { backgroundColor: `var(${colorCssVars.background.normal})` },
        cssForMediaSize({
          min: MediaSize.MEDIUM,
          css: { backgroundColor: `var(${colorCssVars.background.faded})` },
        }),
      ]}
    >
      <CardPageSection
        cardProps={{
          shadow: true,
        }}
        css={cssForMediaSize({
          min: MediaSize.MEDIUM,
          css: { marginTop: spacing.xxl },
        })}
      >
        <Grid>
          <div css={verticalStackCss.m}>
            {nonprofit.hasAdmin && !inviteToken ? (
              <React.Fragment>
                <Headline>
                  <span>{nonprofit.name}</span>
                  <br />
                  already has an admin.
                </Headline>
                {authState.status === AuthStatus.LOGGED_OUT && (
                  <p>
                    Are you the admin?{" "}
                    <ExistingUserLoginLink redirectUrl={redirectUrl} />
                  </p>
                )}
                <p>
                  {authState.status === AuthStatus.LOGGED_OUT
                    ? "Otherwise, please"
                    : "Please"}{" "}
                  reach out to your organization’s admin and ask them to invite{" "}
                  {loggedInUserOrUndefined
                    ? `@${loggedInUserOrUndefined.username} by username`
                    : "you"}
                  .
                </p>
                <p>
                  If you are seeing this message in error, please{" "}
                  <ContactUsLink />.
                </p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Headline>
                  Become an admin of <br />
                  <span>{nonprofit.name}</span>
                </Headline>
                {isLoggedIn ? (
                  <p>Click Next to request admin control over this profile.</p>
                ) : (
                  <p>
                    To become an admin for this nonprofit you’ll need to set up
                    a personal profile on Every.org (or log in to your profile
                    if you’ve already made one). After creating your personal
                    profile you’ll be able to access and update your nonprofit’s
                    info.
                  </p>
                )}
                <p>
                  Note: Once we’ve verified your profile, you will be able to
                  invite other members of your organization to become admins.
                </p>
                <Button
                  data-tname="Next_OnboardingLink"
                  // Next can trigger an admin-access request after navigation or signup.
                  data-bloom-block
                  role={ButtonRole.PRIMARY}
                  css={{ alignSelf: "flex-end" }}
                  onClick={{
                    kind: ButtonTargetKind.FUNCTION,
                    action: nextAction,
                  }}
                >
                  Next
                </Button>
              </React.Fragment>
            )}
          </div>
          <NonprofitCardContainer>
            <div css={{ position: "relative", width: "100%" }}>
              <CloudinaryImg
                cloudinaryId={NP_CARD_BG_TOP_CLOUDINARY_ID}
                alt="background image"
                imageFormat={ImageFormat.svg}
                dimensions={{ width: 172, height: 177 }}
                css={[
                  css`
                    height: 177px;
                    top: -47px;
                    right: -39px;
                    position: absolute;
                    display: none;
                  `,
                  cssForMediaSize({
                    min: MediaSize.LARGE,
                    css: { display: "block" },
                  }),
                ]}
              />
              <CloudinaryImg
                cloudinaryId={NP_CARD_BG_BOTTOM_CLOUDINARY_ID}
                alt="background image"
                imageFormat={ImageFormat.svg}
                dimensions={{ width: 101, height: 142 }}
                css={[
                  css`
                    height: 142px;
                    bottom: -28px;
                    left: -37px;
                    position: absolute;
                    display: none;
                  `,
                  cssForMediaSize({
                    min: MediaSize.LARGE,
                    css: { display: "block" },
                  }),
                ]}
              />
              <NewNonprofitCard
                nonprofit={nonprofit}
                causeCategoryBackgroundColor
                showButtons={false}
                css={{ width: "100%" }}
              />
            </div>
            {nonprofit.ein && isValidEin(nonprofit.ein) && (
              <p
                css={{
                  color: `var(${colorCssVars.text.secondary})`,
                  position: "relative",
                  zIndex: 100,
                }}
              >
                EIN: {formatEinWithDash(nonprofit.ein)}
              </p>
            )}
            <p css={{ textAlign: "center", zIndex: 100 }}>
              Not the right nonprofit?{" "}
              <Link
                data-tname={"ReturnToSearch_OnboardingLink"}
                to={getRoutePath({
                  name: ClientRouteName.NONPROFITS_ONBOARDING_SEARCH,
                  format: URLFormat.RELATIVE,
                  query: {
                    ...(referral ? { referral } : {}),
                  },
                })}
              >
                Return to search
              </Link>
            </p>
          </NonprofitCardContainer>
        </Grid>
        {campaign?.type === NonprofitAdminReferralType.MAILER_REWARD && (
          <ReferredNonprofitCard campaign={campaign} />
        )}
      </CardPageSection>
    </DefaultPageLayout>
  );
};

export default NonprofitOnboardingIntro;
